import { NegotiationDetail, NegotiationSide, NegotiationType, NegotiationDetailWithMeta, IsBestQuote } from 'api'
import { isNil, first, isEmpty } from 'lodash'
import { getMetaValueForWindow } from 'model'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { BestPriceType } from 'store/grid/gridTypes'
import { getCurrentWindowId } from 'utils'
import { bestPriceTypeSelector } from '../gridSelectors'
import {
  groupedByParentOrderIdInitiatedNegotiationsSelector,
  groupedByCollectionIdInitiatedNegotiationsSummarySelector,
  initiatedItemNegotiationsSelector
} from './negotiationSelectors'
import { negotiationRepositoryEntryGetterSelector } from './negotiationRepositoryEntrySelectors'
import { bestPricesByParentIdGetterSelector } from 'selectors/bestPriceSelectors'
import { isNotNil } from 'utils/predicate'

export const isCounterpartySelectedField = 'isCounterpartySelected'

/* Counterparty View Selected Negotiation Selectors */

export const createUserSelectedNegotiationDetail = (parentOrderId?: string): Selector<GlobalState, NegotiationDetail | undefined> =>
  createSelector(
    negotiationRepositoryEntryGetterSelector,
    groupedByParentOrderIdInitiatedNegotiationsSelector,
    (getNegotiationRepositoryEntries, negotiations) => {
      if (isNil(parentOrderId) || !negotiations[parentOrderId]) {
        return undefined
      }

      const initiatorEntries = getNegotiationRepositoryEntries(NegotiationSide.INITIATOR)

      const selectedNegotiation = negotiations[parentOrderId].find(({ negotiation }) => {
        const negotiationUpdate = initiatorEntries[negotiation.negotiationId]
        return getMetaValueForWindow<boolean>(negotiationUpdate, isCounterpartySelectedField, getCurrentWindowId())
      })

      return selectedNegotiation
    }
  )

const createUserSelectedNegotiationDetailSummary = (
  negotiationDetail: NegotiationDetail
): Selector<GlobalState, NegotiationDetail | undefined> =>
  createSelector(
    negotiationRepositoryEntryGetterSelector,
    groupedByCollectionIdInitiatedNegotiationsSummarySelector,
    (getNegotiationRepositoryEntries, negotiations) => {
      const collectionId = negotiationDetail.negotiation.collectionId
      if (isNil(collectionId) || !negotiations[collectionId]) {
        return undefined
      }

      const initiatorEntries = getNegotiationRepositoryEntries(NegotiationSide.INITIATOR)

      const selectedNegotiation = negotiations[collectionId].find(({ negotiation }) => {
        const negotiationUpdate = initiatorEntries[negotiation.negotiationId]
        return getMetaValueForWindow<boolean>(negotiationUpdate, isCounterpartySelectedField, getCurrentWindowId())
      })

      return selectedNegotiation
    }
  )

export const createSelectedNegotiationDetailSummary = (
  negotiationDetail: NegotiationDetail
): Selector<GlobalState, NegotiationDetail | undefined> =>
  createSelector(
    createUserSelectedNegotiationDetailSummary(negotiationDetail),
    createBestOrderNegotiationDetailSummary(negotiationDetail),
    (userSelectedNegotiationSummary, bestNegotiationSummary) => userSelectedNegotiationSummary || bestNegotiationSummary
  )

const createAONBestOrderNegotiationDetailSelector = (negotiationDetail: NegotiationDetail) =>
  createSelector(
    createBestOrderNegotiationDetailSummary(negotiationDetail),
    initiatedItemNegotiationsSelector,
    (bestOrderNegotiationSummary, allInitiatedItems) =>
      // find the best priced leg by locating best priced summary, then locating the summary leg that matches the parentorderid.
      // might return the same leg that was supplied, or a parent order sibling
      // see AON.puml
      allInitiatedItems.find(
        ({ negotiation }) =>
          negotiation.summaryNegotiationId === bestOrderNegotiationSummary?.negotiation.negotiationId &&
          negotiation.parentOrderId === negotiationDetail.negotiation.parentOrderId
      )
  )

export const createBestOrderNegotiationDetailSelector = (negotiationDetail: NegotiationDetail) =>
  createSelector(
    createDefaultBestOrderNegotiationDetailSelector(negotiationDetail),
    createAONBestOrderNegotiationDetailSelector(negotiationDetail),
    (defaultBestOrderNegotiationDetailSelector, aonBestOrderNegotiationDetailSelector) => {
      return negotiationDetail.negotiation.negotiationType === NegotiationType.CONTINGENT_ITEM
        ? aonBestOrderNegotiationDetailSelector
        : defaultBestOrderNegotiationDetailSelector
    }
  )

export const createSelectedNegotiationDetailLeg = (
  negotiationDetail: NegotiationDetail
): Selector<GlobalState, NegotiationDetail | undefined> =>
  createSelector(
    createUserSelectedNegotiationDetail(negotiationDetail.negotiation.parentOrderId),
    createBestOrderNegotiationDetailSelector(negotiationDetail),
    (userSelectedNegotiation, bestNegotiation) => userSelectedNegotiation || bestNegotiation
  )

export const createSelectedNegotiationDetailSummaryWithMeta = (
  negotiationDetail: NegotiationDetail
): Selector<GlobalState, NegotiationDetailWithMeta | undefined> =>
  createSelector(
    createSelectedNegotiationDetailSummary(negotiationDetail),
    createIsBestOrderNegotiationDetailSummarySelected(negotiationDetail),
    (selectedNegotiationSummary, isBestQuote) => {
      if (isNil(selectedNegotiationSummary)) {
        return selectedNegotiationSummary
      }

      return {
        ...selectedNegotiationSummary,
        meta: {
          isBestQuote
        }
      }
    }
  )

/* Best Quote Negotiation Selectors */

const createBestOrdersNegotiationDetail = (
  negotiationDetail: NegotiationDetail,
  count = 1
): Selector<GlobalState, NegotiationDetail[] | undefined> =>
  createSelector(
    groupedByParentOrderIdInitiatedNegotiationsSelector,
    bestPricesByParentIdGetterSelector,
    bestPriceTypeSelector,
    (negotiations, getPricesByParentId) => {
      const parentOrderId = negotiationDetail.negotiation.parentOrderId
      if (isNil(parentOrderId) || !negotiations[parentOrderId]) {
        return undefined
      }

      const prices = getPricesByParentId(parentOrderId, count)

      const negotiationsBestPrices = prices
        .map(p => {
          const negotiation = negotiations[parentOrderId].find(n => n.negotiation.negotiationId === p.entityId)

          return negotiation
        })
        .filter(isNotNil)

      return negotiationsBestPrices
    }
  )

const createBestOrdersNegotiationDetailSummary = (
  negotiationDetail: NegotiationDetail,
  count = 1
): Selector<GlobalState, NegotiationDetail[] | undefined> =>
  createSelector(
    groupedByCollectionIdInitiatedNegotiationsSummarySelector,
    bestPricesByParentIdGetterSelector,
    (negotiations, getPricesByParentId) => {
      const collectionId = negotiationDetail.negotiation.collectionId
      if (isNil(collectionId) || !negotiations[collectionId]) {
        return undefined
      }

      const prices = getPricesByParentId(collectionId, count)

      const negotiationsBestPrices = prices
        .map(p => {
          const negotiation = negotiations[collectionId].find(n => n.negotiation.negotiationId === p.entityId)

          return negotiation
        })
        .filter(isNotNil)

      return negotiationsBestPrices
    }
  )

const createDefaultBestOrderNegotiationDetailSelector = (
  negotiationDetail: NegotiationDetail
): Selector<GlobalState, NegotiationDetail | undefined> =>
  createSelector(createBestOrdersNegotiationDetail(negotiationDetail), bestNegotiations => {
    return first(bestNegotiations)
  })

const createBestOrderNegotiationDetailSummary = (
  negotiationDetail: NegotiationDetail
): Selector<GlobalState, NegotiationDetail | undefined> =>
  createSelector(createBestOrdersNegotiationDetailSummary(negotiationDetail), bestNegotiations => {
    return first(bestNegotiations)
  })

const createIsBestOrderNegotiationDetailSelected = (
  createBestOrdersNegotiationDetailFn: typeof createBestOrdersNegotiationDetail | typeof createBestOrdersNegotiationDetailSummary,
  createSelectedNegotiationDetailFn: typeof createSelectedNegotiationDetailLeg | typeof createUserSelectedNegotiationDetailSummary
) => (negotiationDetail: NegotiationDetail): Selector<GlobalState, IsBestQuote> =>
  createSelector(
    createBestOrdersNegotiationDetailFn(negotiationDetail, 2),
    createSelectedNegotiationDetailFn(negotiationDetail),
    bestPriceTypeSelector,
    (bestNegotiations, selectedNegotiation, priceType) => {
      if (isNil(bestNegotiations) || isEmpty(bestNegotiations) || isNil(selectedNegotiation)) {
        return IsBestQuote.NO
      }

      const negotiationA = bestNegotiations[0]
      const negotiationB = bestNegotiations[1]

      // If there's only a single quoted negotiation, does the selected one match it?
      if (bestNegotiations.length === 1) {
        return negotiationA === selectedNegotiation ? IsBestQuote.YES : IsBestQuote.NO
      }

      const {
        negotiation: {
          rate: { negotiated: negotiatedRateA },
          repoInterest: repoInterestA
        }
      } = negotiationA
      const {
        negotiation: {
          rate: { negotiated: negotiatedRateB },
          repoInterest: repoInterestB
        }
      } = negotiationB

      const comparisonFieldA = priceType === BestPriceType.RATE ? negotiatedRateA : repoInterestA
      const comparisonFieldB = priceType === BestPriceType.RATE ? negotiatedRateB : repoInterestB

      // If there's matching best quoted negotiations, does the selected one match them?
      if (comparisonFieldA === comparisonFieldB) {
        const {
          rate: { negotiated },
          repoInterest
        } = selectedNegotiation.negotiation

        const selectedComparisonField = priceType === BestPriceType.RATE ? negotiated : repoInterest

        return comparisonFieldA === selectedComparisonField ? IsBestQuote.MATCHED : IsBestQuote.NO
      }

      // If there's multiple quoted negotiations but only one is best, does the selected one match it?
      return negotiationA === selectedNegotiation ? IsBestQuote.YES : IsBestQuote.NO
    }
  )

export const createIsBestOrderNegotiationDetailLegSelected = createIsBestOrderNegotiationDetailSelected(
  createBestOrdersNegotiationDetail,
  createSelectedNegotiationDetailLeg
)

const createIsBestOrderNegotiationDetailSummarySelected = createIsBestOrderNegotiationDetailSelected(
  createBestOrdersNegotiationDetailSummary,
  createSelectedNegotiationDetailSummary
)

export const createSelectedNegotiationDetailWithMeta = (
  negotiationDetail: NegotiationDetail
): Selector<GlobalState, NegotiationDetailWithMeta | undefined> =>
  createSelector(
    createSelectedNegotiationDetailLeg(negotiationDetail),
    createIsBestOrderNegotiationDetailLegSelected(negotiationDetail),
    (selectedNegotiation, isBestQuote) => {
      if (isNil(selectedNegotiation)) {
        return selectedNegotiation
      }

      return {
        ...selectedNegotiation,
        meta: {
          isBestQuote
        }
      }
    }
  )
